<template> 
  <div class="customers">
    <header class="head"> 
      <input class="search" v-model="search" :placeholder="$t('advanced.customers.search_placeholder')" />
      <button class="primary" @click="openModal('CustomerAdd')">
        <img class="icon" src="../../randevu/icons/plusw.svg" :alt="$t('advanced.customers.new_icon_alt')" />
        {{ $t('advanced.customers.new') }}
      </button>
    </header>

    <div v-if="loading" class="info">{{ $t('common.loading') }}</div>
    <div v-if="error" class="error">{{ error }}</div>

    <div class="cards">
      <div v-for="c in filtered" :key="c.id" class="card">
        <div class="left">
          <h3>{{ c.name }}</h3>
          <p>{{ c.email || '-' }}</p>
          <p>{{ c.phone || '-' }}</p>
        </div>
        <div class="right">
          <button class="btn-icon" @click="openModal('CustomerDetail', c.id)">
            <img src="../../randevu/icons/goruntule.svg" :alt="$t('advanced.customers.view_alt')" />
          </button>
          <button class="btn-icon" @click="openModal('CustomerAdd', c)">
            <img src="../../randevu/icons/duzenle.svg" :alt="$t('advanced.customers.edit_alt')" />
          </button>
          <button class="btn-icon" @click="confirmDelete(c.id)">
            <img src="../../randevu/icons/sil.svg" :alt="$t('advanced.customers.delete_alt')" />
          </button>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click="overlay">
      <div ref="modal" class="modal-content" :style="style" @click.stop>
        <div class="close-div">
          <button class="close-button" @click="close">X</button>
        </div>
        <component
          :is="current"
          :customer="selected"
          :customerId="selectedId"
          @refresh="fetch"
          @close="close"
        />
      </div>
    </div>
  </div> 
</template>

<script>
import { ref, computed, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import axios from "axios";
import CustomerAdd from "./CustomerAdd.vue";
import CustomerDetail from "./CustomerDetail.vue";

export default {
  name: "MyCustomers",
  components: { CustomerAdd, CustomerDetail },
  setup() {
    const { t } = useI18n();
    const customers = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const showModal = ref(false);
    const current = ref(null);
    const selected = ref(null);
    const selectedId = ref(null);
    const style = ref({ transform: "translateY(100%)", transition: "transform .3s ease" });

    const search = ref("");
    const filtered = computed(() =>
      customers.value.filter(c => c.name.toLowerCase().includes(search.value.toLowerCase()))
    );

    const fetch = async () => {
      loading.value = true;
      error.value = null;
      try {
        const { data } = await axios.get("/promo/user/my-customers");
        customers.value = data;
      } catch (err) {
        if (err.response && err.response.status === 404) { 
          error.value = t("advanced.customers.empty");
          customers.value = [];
        } else {
          error.value = t("advanced.customers.fetch_error");
        }
      } finally {
        loading.value = false;
      }
    };
    
    const openModal = (comp, customer = null) => {
      current.value = comp;
      selected.value = typeof customer === "object" ? customer : null;
      selectedId.value = typeof customer === "number" ? customer : null;
      showModal.value = true;
      nextTick(() => setTimeout(() => (style.value.transform = "translateY(0)"), 10));
    };

    const close = () => {
      style.value.transform = "translateY(100%)";
      setTimeout(() => { showModal.value = false; current.value = null; }, 400);
    };

    const overlay = e => { if (e.target === e.currentTarget) close(); };

    const confirmDelete = async id => {
      if (!confirm(t("advanced.customers.delete_confirm"))) return;
      try {
        await axios.delete(`/promo/user/my-customers/${id}`);
        await fetch();
      } catch {
        error.value = t("advanced.customers.delete_error");
      }
    };

    onMounted(fetch);

    return {
      customers,
      loading,
      error,
      search,
      filtered,
      openModal,
      showModal,
      current,
      selected,
      selectedId,
      style,
      close,
      overlay,
      confirmDelete,
      fetch
    };
  }
};
</script>

<style scoped>  
.head {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 1em;
}

.search {
  flex: 1 1 180px;
  padding: 8px;   
}

.primary {
  background: linear-gradient(45deg, #086B68, #086B68,  #022121); 
  border: solid 1px #086B68;
  cursor: pointer;
  border-radius: 0;
  transition: all 0.3s ease; 
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px;
  color: white;
}
.primary:hover {
  border-color: #1d964d;
  color: #e4e4e4;
  transform: scale(1.05); 
}
.icon {
  max-width: 1rem;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit );
  gap: 0.5em; 
  max-height: 480px;
  overflow: auto;   
}

.card {
  background: linear-gradient(145deg, #020202, #0a0a0a);
  border: 1px solid #1a1a1a;
  border-radius: 4px;
  padding: 1em;
  color: #ddd;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
}

.card h3 {
  margin: 0 0 6px 0;
  font-weight: 300;
  font-size: 1.rem;
}

.card p {
  margin: 3px 0;
}

.left {
  flex: 1 1 auto;
  min-width: 60%;
}

.right {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  flex-grow: 1;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
}
.btn-icon img {
  width: 20px;
  height: 20px;
  filter: brightness(1.2); 
  transition: all 0.2s ease; 
}
.btn-icon:hover img {  transform: scale(1.1);
  filter: brightness(1.5);
} 

.info {
  margin-top: 12px;
  color: #aaa;
}

.error {
  font-weight: 500;
  margin-top: 12px;
  color: #917E5C;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
}

.modal-content {
  margin-top: 12rem;
  width: 100%;
  max-width: 768px;
  background: linear-gradient(45deg, #020202, #080808, #030b0b);
  border-radius: 10px 10px 0 0;
  border-top: 1px solid #022121;
  border-left: 1px solid #022121;
  border-right: 1px solid #022121;
  padding: 1rem;
  overflow-y: auto;
  max-height: 80vh;
}

.close-div {
  position: sticky;
  border-radius: 15px 15px 0 0;
  top: 0;
  display: flex;
  justify-content: flex-end;
  z-index: 1;
  padding: 0.5rem;
}

.close-button {
  background-color: #000000;
  color: #ffffff;
  padding: 0.8rem;
  min-width: 3rem;
  max-width: 3rem;
  border-radius: 2rem;
  opacity: 0.8;
  border: 1px solid #444444;
  cursor: pointer;
  transition: transform 0.3s ease;
}
.close-button:hover {
  opacity: 1;
  transform: scale(1.1);
}
</style>
